import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { API } from "./shared";
import { flowState, openPortfolioAssessment } from "./portfolioFlow";

// Portfolio health is based on the PORTFOLIO's own assessment — never the school's score.
export const pfScore = (s) => {
  const pa = s.portfolio_assessment || {};
  return pa.has_assessment && pa.score !== null && pa.score !== undefined ? pa.score : null;
};
export const pfProvisional = (s) => pfScore(s) !== null && !(s.portfolio_assessment || {}).is_final;
export const bandOf = (v) => (v === null || v === undefined ? null : v >= 80 ? "healthy" : v >= 60 ? "caution" : "high_risk");
const BAND = {
  healthy: { label: "Healthy", sub: "80 and above", bar: "#15803d", pill: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  caution: { label: "Caution", sub: "60 to 79", bar: "#b45309", pill: "bg-amber-50 text-amber-800 border-amber-200" },
  high_risk: { label: "High Risk", sub: "Below 60", bar: "#b91c1c", pill: "bg-red-50 text-red-800 border-red-200" },
};

export const healthStats = (schools) => {
  const scored = schools.filter((s) => pfScore(s) !== null);
  const c = { healthy: 0, caution: 0, high_risk: 0 };
  scored.forEach((s) => { c[bandOf(pfScore(s))] += 1; });
  const avg = scored.length ? Math.round((scored.reduce((a, s) => a + pfScore(s), 0) / scored.length) * 10) / 10 : null;
  const final = schools.filter((s) => (s.portfolio_assessment || {}).is_final).length;
  return { ...c, awaiting: schools.length - scored.length, avg, final, total: schools.length, scored: scored.length };
};

export const PortfolioHealth = ({ stats, periodName, active, onPick }) => (
  <section className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5" data-testid="portfolio-health">
    <div className="flex flex-wrap items-baseline justify-between gap-2">
      <h2 className="text-lg font-semibold text-slate-900">Portfolio health</h2>
      {stats.awaiting > 0 && <span className="text-xs text-slate-600" data-testid="health-awaiting">{stats.awaiting} awaiting your assessment</span>}
    </div>
    <p className="mt-0.5 text-xs text-slate-600">{periodName ? `${periodName} · ` : ""}based on your assessments · Healthy ≥80 · Caution 60–79 · High Risk &lt;60</p>
    <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
      {["healthy", "caution", "high_risk"].map((k) => (
        <button key={k} onClick={() => onPick(k)} aria-pressed={active === k} data-testid={`health-tile-${k}`}
          className={`min-h-[44px] rounded-lg border bg-white p-3 text-left transition-shadow hover:shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#007a9e] ${active === k ? "ring-2 ring-[#007a9e]" : "border-slate-200"}`}
          style={{ borderLeft: `4px solid ${BAND[k].bar}` }}>
          <div className="text-3xl font-bold text-slate-900" data-testid={k === "high_risk" ? "portfolio-card-high-risk" : undefined}>{stats[k]}</div>
          <div className="text-sm font-semibold text-slate-900">{BAND[k].label}</div>
          <div className="text-xs text-slate-600">{BAND[k].sub}</div>
        </button>
      ))}
    </div>
  </section>
);

export const ProvisionalTag = () => (
  <span className="ml-1.5 inline-block w-[78px] shrink-0 rounded-full border border-amber-300 bg-amber-50 text-center text-[10px] font-semibold text-amber-800">Provisional</span>
);

const ScoreCell = ({ v, provisional, testid }) => {
  const b = bandOf(v);
  if (b === null) return <span className="text-xs text-slate-600" data-testid={testid}>—</span>;
  return (
    <span className="inline-flex flex-wrap items-center gap-1" data-testid={testid}>
      <b className="tabular-nums text-slate-900">{v.toFixed(1)}</b>
      <span className={`rounded-full border px-1.5 text-[10.5px] font-semibold ${BAND[b].pill}`}>{BAND[b].label}</span>
      {provisional && <ProvisionalTag />}
    </span>
  );
};

// Latest non-closed period (draft or open) that includes the school.
const assessPeriod = (periods, tid) => (periods || [])
  .filter((p) => p.status !== "closed" && ((p.school_ids || []).includes(tid) || (p.requests || []).some((q) => q.tenant_id === tid)))
  .sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || "")))[0];

const AssessLink = ({ s, periods, pid }) => {
  const navigate = useNavigate();
  const [opening, setOpening] = useState(false);
  const tid = s.tenant_id;
  const cls = "min-h-[44px] sm:min-h-0 text-left text-xs font-semibold text-[#6b3fa0] underline hover:text-[#4c2a78] disabled:opacity-60";
  const per = assessPeriod(periods, tid);
  if (!per) {
    return <button onClick={() => navigate(`/portfolio/periods?new=1&school=${tid}&pid=${pid}&from=dashboard`)} className={cls} data-testid={`school-score-pf-${tid}`}>Create a period to start your assessment</button>;
  }
  if (!per.enable_portfolio_assessments) {
    return <span className="text-xs text-slate-600" data-testid={`school-score-pf-${tid}`}>Not included in {per.name}</span>;
  }
  const req = (per.requests || []).find((q) => q.tenant_id === tid);
  const started = flowState(req?.portfolio_status) !== "not_started";
  const open = async () => {
    setOpening(true);
    try { await openPortfolioAssessment(pid, per.id, tid); } catch (e) { toast.error(e.message); setOpening(false); }
  };
  return (
    <button onClick={open} disabled={opening} className={cls} data-testid={`school-score-pf-${tid}`}>
      {opening ? "Opening…" : started ? "Continue your assessment" : "Start your assessment"}
    </button>
  );
};

// Per-school health list, lowest Portfolio score first; awaiting schools listed (not counted) at the end.
export const SchoolScoresList = ({ schools, band, periods, pid }) => {
  const navigate = useNavigate();
  const rows = schools.filter((s) => !band || bandOf(pfScore(s)) === band)
    .sort((a, b) => (pfScore(a) ?? 1e9) - (pfScore(b) ?? 1e9));
  return (
    <div data-testid="school-scores-list">
      <div className="msx-hgrid hidden px-3 pb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-600 sm:grid">
        <span>School</span><span>Your assessment</span><span>School's diagnostic</span>
      </div>
      <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
        {rows.map((s) => (
          <li key={s.tenant_id} className="msx-hgrid px-3 py-2.5" data-testid={`school-score-row-${s.tenant_id}`}>
            <button onClick={() => navigate(`/portfolio/schools/${s.tenant_id}`)} className="min-h-[44px] sm:min-h-0 text-left text-sm font-semibold text-[#00607d] underline">{s.school_name}</button>
            {pfScore(s) === null
              ? <AssessLink s={s} periods={periods} pid={pid} />
              : <ScoreCell v={pfScore(s)} provisional={pfProvisional(s)} testid={`school-score-pf-${s.tenant_id}`} />}
            <ScoreCell v={s.score ?? null} provisional={!s.assessment_is_final} testid={`school-score-school-${s.tenant_id}`} />
          </li>
        ))}
        {!rows.length && <li className="px-3 py-4 text-sm text-slate-600">No schools in this band.</li>}
      </ul>
    </div>
  );
};

// Open/closed state per user (server-side), localStorage only as a cache. Default: all collapsed.
export const useDashSections = (pid) => {
  const [open, setOpen] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`msrx.dashSections.${pid}`) || "[]"); } catch { return []; }
  });
  useEffect(() => {
    if (!pid) return;
    fetch(`${API}/api/portfolio/${pid}/me/dashboard-sections`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null)).then((d) => {
        if (!d) return;
        const keys = d.open || [];
        setOpen(window.matchMedia("(max-width: 480px)").matches ? keys.slice(0, 1) : keys);
      }).catch(() => {});
  }, [pid]);
  const save = useCallback((next) => {
    setOpen(next);
    localStorage.setItem(`msrx.dashSections.${pid}`, JSON.stringify(next));
    fetch(`${API}/api/portfolio/${pid}/me/dashboard-sections`, {
      method: "PUT", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ open: next }),
    }).catch(() => {});
  }, [pid]);
  const toggle = useCallback((key, force) => {
    const isOpen = open.includes(key);
    const want = force ?? !isOpen;
    if (want === isOpen) return;
    const phone = window.matchMedia("(max-width: 480px)").matches;
    save(want ? (phone ? [key] : [...open, key]) : open.filter((k) => k !== key));
  }, [open, save]);
  return { open, toggle, setAll: (keys) => save(keys) };
};

export const DashSection = ({ id, title, summary, open, onToggle, children }) => {
  const uid = useId();
  const ref = useRef(null);
  return (
    <section ref={ref} id={`dash-${id}`} className="scroll-mt-4 rounded-xl border border-slate-200 bg-white" data-testid={`dash-section-${id}`}>
      <h2 className="m-0">
        <button type="button" aria-expanded={open} aria-controls={`${uid}-panel`} id={`${uid}-head`} onClick={onToggle}
          data-testid={`dash-section-toggle-${id}`}
          className="flex min-h-[56px] w-full items-center justify-between gap-3 rounded-xl px-4 py-3 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#007a9e]">
          <span className="min-w-0">
            <span className="block text-base font-semibold text-slate-900">{title}</span>
            <span className="mt-0.5 block text-[13px] text-slate-600" data-testid={`dash-section-summary-${id}`}>{summary}</span>
          </span>
          <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-[#e6f4f8]">
            <ChevronRight className={`h-4 w-4 text-[#007a9e] motion-safe:transition-transform ${open ? "rotate-90" : ""}`} aria-hidden />
          </span>
        </button>
      </h2>
      <div id={`${uid}-panel`} role="region" aria-labelledby={`${uid}-head`} hidden={!open} className="border-t border-slate-100 p-4">
        {children}
      </div>
    </section>
  );
};
